import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { api, ApiError, type ShiftSlotView, type TeamGroup, type TeamMemberView } from '../lib/api'
import { Banner, Button, Card, Chip, Switch } from '../ui'
import { ReadOnlyNotice } from './ReadOnlyNotice'

const ROLE_COPY: Record<string, { label: string; detail: string }> = {
  owner: { label: 'Owner', detail: 'Full access, including billing' },
  admin: { label: 'Admin', detail: 'Sees every conversation and manages the team' },
  agent: { label: 'Agent', detail: 'Sees and replies to the labels you choose' },
  viewer: { label: 'Viewer', detail: 'Reads the labels you choose, cannot reply' },
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MAX_SHIFTS_PER_DAY = 3

const minutesToTime = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
const timeToMinutes = (t: string) => {
  const [h, m] = t.split(':').map(Number)
  return h * 60 + m
}
const browserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone

/**
 * Team management, where access is granted by label rather than per conversation.
 *
 * The screen is built around one question — who can see what — because that is the thing
 * an admin gets wrong. Every row states its scope in words rather than leaving it implied
 * by a checkbox somewhere else.
 */
export function Team() {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('agent')
  const [selectedLabels, setSelectedLabels] = useState<string[]>([])
  const [inviteResults, setInviteResults] = useState<
    { email: string; ok: boolean; url?: string; emailed?: boolean; error?: string }[] | null
  >(null)
  const [editing, setEditing] = useState<string | null>(null)
  const [selectedForBulk, setSelectedForBulk] = useState<string[]>([])

  const session = useQuery({ queryKey: ['session'], queryFn: api.session })
  const team = useQuery({ queryKey: ['team'], queryFn: api.team, retry: false })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['team'] })

  const invite = useMutation({
    // Sent one at a time rather than in parallel: the server checks seats-left per call, so a
    // batch of five against three remaining seats needs to see its own earlier successes land
    // before it can correctly refuse the fourth — a race that Promise.all would not respect.
    mutationFn: async (addresses: string[]) => {
      const results: { email: string; ok: boolean; url?: string; emailed?: boolean; error?: string }[] = []
      for (const address of addresses) {
        try {
          const result = await api.invite({ email: address, role, labelIds: selectedLabels })
          results.push({ email: address, ok: true, url: result.inviteUrl, emailed: result.emailed })
        } catch (err) {
          const message = err instanceof ApiError || err instanceof Error ? err.message : 'Could not invite.'
          results.push({ email: address, ok: false, error: message })
        }
      }
      return results
    },
    onSuccess: async (results) => {
      // mutationFn never throws — a per-address failure (e.g. "choose a label") lands here as
      // ok: false, not as a rejected mutation — so clearing unconditionally wiped the address the
      // user still needs to fix and resubmit. Keep failed addresses in the box; only clear (and
      // drop the now-satisfied label requirement) once every address in the batch went through.
      const failed = results.filter((r) => !r.ok)
      setEmail(failed.length ? failed.map((r) => r.email).join('\n') : '')
      if (failed.length === 0) setSelectedLabels([])
      // The link is always shown, even when the email went, so the admin can pass it on directly
      // if the invitee says it never arrived — email is the convenience, the link is the guarantee.
      setInviteResults(results)
      await refresh()
    },
  })

  const privacy = useMutation({ mutationFn: api.setPrivacy, onSuccess: refresh })
  const revoke = useMutation({ mutationFn: api.revokeInvite, onSuccess: refresh })
  const remove = useMutation({ mutationFn: api.removeMember, onSuccess: refresh })
  const updateAccess = useMutation({
    mutationFn: (input: { userId: string; role: string; labelIds: string[]; numberIds?: string[] }) =>
      api.updateAccess(input.userId, { role: input.role, labelIds: input.labelIds, numberIds: input.numberIds }),
    onSuccess: async () => {
      setEditing(null)
      await refresh()
    },
  })
  const updateShifts = useMutation({
    mutationFn: (input: { userId: string; shifts: ShiftSlotView[] }) =>
      api.updateShifts(input.userId, input.shifts),
    onSuccess: refresh,
  })
  const setOwnPresence = useMutation({ mutationFn: api.setPresence, onSuccess: refresh })
  const setBulkPresence = useMutation({
    mutationFn: (status: 'online' | 'offline') => api.setPresenceBulk(selectedForBulk, status),
    onSuccess: async () => {
      setSelectedForBulk([])
      await refresh()
    },
  })

  if (team.isPending) return <div className="skeleton" style={{ height: '240px' }} />
  if (team.isError) {
    const message = team.error instanceof ApiError ? team.error.message : 'Team unavailable.'
    return <Banner tone="info">{message}</Banner>
  }

  const data = team.data!
  const labelName = (id: string) => data.labels.find((l) => l.id === id)?.name ?? 'a deleted label'
  const seatsLeft = data.seats.total - data.seats.used
  // One entry per address typed into the box — invalid-looking ones (no @) are dropped rather
  // than sent, so a stray comma or trailing space never turns into a confusing per-row error.
  const parsedEmails = [...new Set(
    email
      .split(/[,\n]/)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e.includes('@')),
  )]

  const numberLabel = (id: string) => {
    const n = data.numbers.find((num) => num.id === id)
    return n ? n.label || n.phone : 'a removed number'
  }

  const scopeOf = (member: TeamMemberView) => {
    if (member.unrestricted) return 'Every conversation'
    if (member.labelIds.length === 0) return 'Nothing yet — no labels granted'
    const labelPart = member.labelIds.map(labelName).join(', ')
    if (member.numberIds.length === 0) return labelPart
    return `${labelPart} · only from ${member.numberIds.map(numberLabel).join(', ')}`
  }

  const me = data.members.find((m) => m.userId === session.data?.user?.id)

  const errorText = (err: unknown, fallback: string) =>
    err instanceof ApiError || err instanceof Error ? err.message : fallback
  // Every member-row action (change access, change shifts, remove, revoke) reported nothing on
  // failure — the button just stopped spinning as if it had worked. One shared banner over the
  // table means whichever of them just failed says so, instead of only `invite` ever explaining
  // itself.
  const memberActionError =
    updateAccess.error ?? updateShifts.error ?? remove.error ?? revoke.error ?? setBulkPresence.error

  return (
    <div className="stack gap-6" style={{ maxWidth: '900px' }}>
      {!data.canManage && <ReadOnlyNotice what="who is on the team or what they can see" onTeamScreen />}
      <div className="stack gap-2">
        <span className="cad-eyebrow">Team</span>
        <h1 className="cad-title">Who can see what</h1>
        <p className="cad-muted" style={{ maxWidth: '62ch' }}>
          Access is granted by label. An agent given only Billing sees billing conversations
          and nothing else, so you can bring in outside help without opening the whole inbox.
        </p>
      </div>

      <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
        <Chip tone={seatsLeft <= 0 ? 'error' : 'assist'}>
          {data.seats.used} of {data.seats.total} seats used
        </Chip>
        {data.invitations.length > 0 && (
          <Chip tone="warn">
            {data.invitations.length} invitation{data.invitations.length === 1 ? '' : 's'} pending
          </Chip>
        )}
        {me && (
          <Button
            variant={me.presence === 'online' ? 'filled' : 'outlined'}
            disabled={setOwnPresence.isPending}
            onClick={() => setOwnPresence.mutate(me.presence === 'online' ? 'offline' : 'online')}
          >
            <span className="presence-dot" data-status={me.presence} aria-hidden="true" />{' '}
            {me.presence === 'online' ? 'You are online' : 'You are offline'}
          </Button>
        )}
      </div>

      {data.canManage && (
        <Card variant="outlined">
          <div className="stack gap-3">
            {privacy.error && <Banner tone="error">{errorText(privacy.error, 'Could not save that.')}</Banner>}
            <div className="row between wrap gap-3">
              <div className="stack gap-1">
                <span className="cad-label">Hide customer phone numbers</span>
                <p className="cad-muted" style={{ maxWidth: '56ch' }}>
                  Agents and viewers see a stable contact reference instead of the number.
                  Admins are unaffected. Use this when outside help works your queues and you
                  do not want your contact list leaving with them.
                </p>
              </div>
              <Switch
                label="Hide customer phone numbers"
                checked={data.maskNumbers}
                disabled={privacy.isPending}
                onChange={() =>
                  privacy.mutate({
                    maskNumbers: !data.maskNumbers,
                    revealDeletedMessages: data.revealDeletedMessages,
                    restrictOutgoingNumber: data.restrictOutgoingNumber,
                    hideAgentName: data.hideAgentName,
                  })
                }
              />
            </div>

            {data.maskNumbers && (
              <div className="stack gap-3" style={{ borderTop: '1px solid var(--md-outline)', paddingTop: '12px' }}>
                <p className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                  Three more specific switches, each only doing anything for the same masked
                  teammates the toggle above applies to.
                </p>
                {(
                  [
                    {
                      key: 'revealDeletedMessages' as const,
                      label: 'Reveal WhatsApp-deleted message content',
                      detail: 'Off by default: a masked teammate sees "This message was deleted." Turn this on to let them see what it said anyway.',
                    },
                    {
                      key: 'restrictOutgoingNumber' as const,
                      label: 'Hide which connected number replied',
                      detail: 'Useful once you have more than one number and do not want a masked teammate to learn which one maps to which brand or region.',
                    },
                    {
                      key: 'hideAgentName' as const,
                      label: 'Hide which teammate replied',
                      detail: 'A masked teammate sees that a reply came from the team, not who on it sent it.',
                    },
                  ] as const
                ).map((toggle) => (
                  <div key={toggle.key} className="row between wrap gap-3">
                    <div className="stack gap-1">
                      <span className="cad-label">{toggle.label}</span>
                      <p className="cad-muted" style={{ maxWidth: '56ch', fontSize: 'var(--cad-text-md)' }}>
                        {toggle.detail}
                      </p>
                    </div>
                    <Switch
                      label={toggle.label}
                      checked={data[toggle.key]}
                      disabled={privacy.isPending}
                      onChange={() =>
                        privacy.mutate({
                          maskNumbers: data.maskNumbers,
                          revealDeletedMessages: data.revealDeletedMessages,
                          restrictOutgoingNumber: data.restrictOutgoingNumber,
                          hideAgentName: data.hideAgentName,
                          [toggle.key]: !data[toggle.key],
                        })
                      }
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      {data.numbers.length > 0 && (
        <Card variant="outlined">
          <div className="stack gap-3">
            <h2 className="cad-section-title">Number labels</h2>
            <p className="cad-muted" style={{ maxWidth: '62ch' }}>
              A separate namespace from chat labels — tagging a number itself, for a workspace
              with more than one, e.g. by brand or region.
            </p>
            {data.numbers.map((number) => (
              <NumberLabelEditor key={number.id} number={number} canManage={data.canManage} />
            ))}
          </div>
        </Card>
      )}

      <Card variant="outlined">
        <div className="stack gap-3">
          {memberActionError && (
            <Banner tone="error">{errorText(memberActionError, 'That did not go through.')}</Banner>
          )}
          <div className="row between wrap gap-3" style={{ alignItems: 'center' }}>
            <h2 className="cad-section-title">Members</h2>
            {data.canManage && selectedForBulk.length > 0 && (
              <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
                <span className="cad-muted">{selectedForBulk.length} selected</span>
                <Button
                  variant="text"
                  disabled={setBulkPresence.isPending}
                  onClick={() => setBulkPresence.mutate('online')}
                >
                  Set online
                </Button>
                <Button
                  variant="text"
                  disabled={setBulkPresence.isPending}
                  onClick={() => setBulkPresence.mutate('offline')}
                >
                  Set offline
                </Button>
              </div>
            )}
          </div>
          <div className="cad-grid-frame">
            <table className="cad-table">
              <thead>
                <tr>
                  {data.canManage && <th />}
                  <th>Name</th>
                  <th>Role</th>
                  <th>Can see</th>
                  {data.canManage && <th />}
                </tr>
              </thead>
              <tbody>
                {data.members.map((member) => (
                  <tr key={member.userId} data-selected={selectedForBulk.includes(member.userId)}>
                    {data.canManage && (
                      <td>
                        {member.role !== 'owner' && (
                          <input
                            type="checkbox"
                            checked={selectedForBulk.includes(member.userId)}
                            onChange={(e) =>
                              setSelectedForBulk((current) =>
                                e.target.checked
                                  ? [...current, member.userId]
                                  : current.filter((id) => id !== member.userId),
                              )
                            }
                          />
                        )}
                      </td>
                    )}
                    <td>
                      <div className="stack gap-1">
                        <span className="row gap-2" style={{ alignItems: 'center' }}>
                          <span className="presence-dot" data-status={member.presence} aria-hidden="true" />
                          <span className="cad-label">{member.name}</span>
                        </span>
                        <span className="cad-muted">{member.email}</span>
                      </div>
                    </td>
                    <td>
                      <Chip tone={member.unrestricted ? 'success' : 'assist'}>
                        {ROLE_COPY[member.role]?.label ?? member.role}
                      </Chip>
                    </td>
                    <td
                      className={member.labelIds.length === 0 && !member.unrestricted ? 'text-muted' : ''}
                      style={{ maxWidth: '32ch', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {scopeOf(member)}
                      {member.shifts.length > 0 && (
                        <div className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                          {member.shifts.length} shift{member.shifts.length === 1 ? '' : 's'} scheduled
                        </div>
                      )}
                    </td>
                    {data.canManage && (
                      <td>
                        {member.role !== 'owner' && (
                          <div className="row gap-2">
                            <Button
                              variant="text"
                              onClick={() =>
                                setEditing(editing === member.userId ? null : member.userId)
                              }
                            >
                              {editing === member.userId ? 'Close' : 'Change'}
                            </Button>
                            <Button
                              variant="text"
                              disabled={remove.isPending}
                              onClick={() => {
                                if (confirm(`Remove ${member.name} from the workspace?

They lose access immediately. Chats assigned to them stay assigned and will need reassigning.`)) remove.mutate(member.userId)
                              }}
                            >
                              Remove
                            </Button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {editing && (
            <AccessEditor
              // Forces a fresh mount per member: the editor seeds its role/label/shift state
              // from `member` only once (useState's initializer), so reusing the instance
              // across a direct A-to-B "Change" click left B's form showing A's stale draft.
              key={editing}
              member={data.members.find((m) => m.userId === editing)!}
              labels={data.labels}
              numbers={data.numbers}
              pending={updateAccess.isPending}
              onSave={(next) => updateAccess.mutate({ userId: editing, ...next })}
              shiftsPending={updateShifts.isPending}
              onSaveShifts={(shifts) => updateShifts.mutate({ userId: editing, shifts })}
            />
          )}
        </div>
      </Card>

      {data.canManage && <TeamGroupsSection teams={data.teams} members={data.members} onChanged={refresh} />}

      {data.invitations.length > 0 && (
        <Card variant="outlined">
          <div className="stack gap-3">
            <h2 className="cad-section-title">Waiting to accept</h2>
            {data.invitations.map((entry) => (
              <div key={entry.id} className="row between wrap gap-3">
                <div className="stack gap-1">
                  <span className="cad-label">{entry.email}</span>
                  <span className="cad-muted">
                    {ROLE_COPY[entry.role]?.label ?? entry.role} ·{' '}
                    {entry.labelIds.length > 0
                      ? entry.labelIds.map(labelName).join(', ')
                      : 'every conversation'}{' '}
                    · expires {new Date(entry.expiresAt).toLocaleDateString()}
                  </span>
                </div>
                {data.canManage && (
                  <Button
                    variant="text"
                    onClick={() => revoke.mutate(entry.id)}
                  >
                    Revoke
                  </Button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {data.canManage && (
        <Card variant="outlined">
          <form
            className="stack gap-4"
            onSubmit={(event: FormEvent) => {
              event.preventDefault()
              setInviteResults(null)
              if (parsedEmails.length > 0) invite.mutate(parsedEmails)
            }}
          >
            <h2 className="cad-section-title">Invite people</h2>

            <div className="cad-field">
              <label htmlFor="invite-emails">Work email — one per line, or comma-separated</label>
              <textarea
                id="invite-emails"
                className="cad-input"
                rows={3}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={'teammate@company.com\nanother@company.com'}
              />
              {parsedEmails.length > 1 && (
                <p className="cad-muted" style={{ fontSize: 'var(--cad-text-base)', margin: '4px 0 0' }}>
                  {parsedEmails.length} people, all with the same role and access below.
                </p>
              )}
            </div>

            <div className="stack gap-2">
              <span className="cad-label">Role</span>
              <div className="stack gap-2">
                {(['admin', 'agent', 'viewer'] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    className="selectable"
                    data-selected={role === option}
                    aria-pressed={role === option}
                    onClick={() => setRole(option)}
                    style={{ textAlign: 'left' }}
                  >
                    <div className="stack gap-1">
                      <span className="cad-label">{ROLE_COPY[option].label}</span>
                      <span className="cad-muted">{ROLE_COPY[option].detail}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {role !== 'admin' && (
              <div className="stack gap-2">
                <span className="cad-label">Which labels can they see?</span>
                {data.labels.length === 0 ? (
                  <Banner tone="info">
                    Connect your workspace database first — labels live there, and without them
                    there is nothing to scope access to.
                  </Banner>
                ) : (
                  <div className="row gap-2 wrap">
                    {data.labels.map((label) => {
                      const on = selectedLabels.includes(label.id)
                      return (
                        <button
                          key={label.id}
                          type="button"
                          className="label-swatch"
                          aria-pressed={on}
                          data-selected={on}
                          onClick={() =>
                            setSelectedLabels((current) =>
                              on ? current.filter((id) => id !== label.id) : [...current, label.id],
                            )
                          }
                        >
                          <span
                            className="label-dot"
                            style={{ background: label.color }}
                            aria-hidden="true"
                          />
                          {label.name}
                        </button>
                      )
                    })}
                  </div>
                )}
                <p className="cad-muted">
                  They will see only conversations carrying these labels. Our rules apply labels
                  automatically, so new chats reach them without anyone tagging by hand.
                </p>
              </div>
            )}

            {inviteResults && (
              <div className="stack gap-2">
                {inviteResults.map((result) => (
                  <Banner key={result.email} tone={result.ok ? 'success' : 'error'}>
                    {result.ok ? (
                      <div className="stack gap-2">
                        <span>
                          {result.emailed
                            ? `Emailed ${result.email}. It expires in 7 days — here is the link too, in case it does not arrive.`
                            : `Invitation created for ${result.email}. Send them this link — it expires in 7 days.`}
                        </span>
                        <code className="invite-link">{result.url}</code>
                      </div>
                    ) : (
                      <span>{result.email}: {result.error}</span>
                    )}
                  </Banner>
                ))}
              </div>
            )}

            <Button
              type="submit"
              size="large"
              disabled={parsedEmails.length === 0 || invite.isPending || seatsLeft <= 0}
            >
              {seatsLeft <= 0
                ? 'No seats left on your plan'
                : invite.isPending
                  ? 'Creating invitations…'
                  : parsedEmails.length > 1
                    ? `Invite ${parsedEmails.length} people`
                    : 'Create invitation'}
            </Button>
          </form>
        </Card>
      )}
    </div>
  )
}

function NumberLabelEditor({
  number,
  canManage,
}: {
  number: { id: string; label: string; phone: string }
  canManage: boolean
}) {
  const queryClient = useQueryClient()
  const labels = useQuery({
    queryKey: ['number-labels', number.id],
    queryFn: () => api.numberLabels(number.id),
    retry: false,
  })
  const [draft, setDraft] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: (names: string[]) => api.setNumberLabels(number.id, names),
    onSuccess: async () => {
      setDraft(null)
      await queryClient.invalidateQueries({ queryKey: ['number-labels', number.id] })
    },
  })

  if (!labels.data) return null
  const current = draft ?? labels.data.labels.map((l) => l.name).join(', ')

  return (
    <div className="row between wrap gap-3" style={{ alignItems: 'center' }}>
      <span className="cad-label">{number.label || number.phone}</span>
      {canManage ? (
        <div className="row gap-2" style={{ alignItems: 'center' }}>
          <input
            className="cad-input"
            value={current}
            placeholder="e.g. Sales, EU"
            onChange={(e) => setDraft(e.target.value)}
          />
          <Button
            variant="text"
            disabled={save.isPending}
            onClick={() =>
              save.mutate(current.split(',').map((n) => n.trim()).filter(Boolean))
            }
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      ) : (
        <span className="cad-muted">{current || 'No labels'}</span>
      )}
    </div>
  )
}

function AccessEditor({
  member,
  labels,
  numbers,
  pending,
  onSave,
  shiftsPending,
  onSaveShifts,
}: {
  member: TeamMemberView
  labels: { id: string; name: string; color: string }[]
  numbers: { id: string; label: string; phone: string }[]
  pending: boolean
  onSave: (next: { role: string; labelIds: string[]; numberIds: string[] }) => void
  shiftsPending: boolean
  onSaveShifts: (shifts: ShiftSlotView[]) => void
}) {
  const [role, setRole] = useState(member.role)
  const [selected, setSelected] = useState<string[]>(member.labelIds)
  const [selectedNumbers, setSelectedNumbers] = useState<string[]>(member.numberIds)

  return (
    <Card variant="filled">
      <div className="stack gap-3">
        <span className="cad-label">Change access for {member.name}</span>

        <div className="row gap-2 wrap">
          {(['admin', 'agent', 'viewer'] as const).map((option) => (
            <button
              key={option}
              type="button"
              className="label-swatch"
              aria-pressed={role === option}
              data-selected={role === option}
              onClick={() => setRole(option)}
            >
              {ROLE_COPY[option].label}
            </button>
          ))}
        </div>

        {role === 'admin' ? (
          <p className="cad-muted">
            Admins see every conversation, so label choices do not apply.
          </p>
        ) : (
          <div className="row gap-2 wrap">
            {labels.map((label) => {
              const on = selected.includes(label.id)
              return (
                <button
                  key={label.id}
                  type="button"
                  className="label-swatch"
                  aria-pressed={on}
                  data-selected={on}
                  onClick={() =>
                    setSelected((current) =>
                      on ? current.filter((id) => id !== label.id) : [...current, label.id],
                    )
                  }
                >
                  <span className="label-dot" style={{ background: label.color }} aria-hidden="true" />
                  {label.name}
                </button>
              )
            })}
          </div>
        )}

        {role !== 'admin' && numbers.length > 1 && (
          <div className="stack gap-2">
            <span className="cad-label">Which connected numbers can they see?</span>
            <div className="row gap-2 wrap">
              {numbers.map((number) => {
                const on = selectedNumbers.includes(number.id)
                return (
                  <button
                    key={number.id}
                    type="button"
                    className="label-swatch"
                    aria-pressed={on}
                    data-selected={on}
                    onClick={() =>
                      setSelectedNumbers((current) =>
                        on ? current.filter((id) => id !== number.id) : [...current, number.id],
                      )
                    }
                  >
                    {number.label || number.phone}
                  </button>
                )
              })}
            </div>
            <p className="cad-muted">
              Leave none selected to allow every connected number — this only narrows things
              down once at least one is chosen, on top of the labels above.
            </p>
          </div>
        )}

        <div className="row gap-2">
          <Button disabled={pending} onClick={() => onSave({ role, labelIds: selected, numberIds: selectedNumbers })}>
            {pending ? 'Saving…' : 'Save access'}
          </Button>
        </div>
      </div>

      <ShiftEditor member={member} pending={shiftsPending} onSave={onSaveShifts} />
    </Card>
  )
}

function ShiftEditor({
  member,
  pending,
  onSave,
}: {
  member: TeamMemberView
  pending: boolean
  onSave: (shifts: ShiftSlotView[]) => void
}) {
  const [shifts, setShifts] = useState<ShiftSlotView[]>(member.shifts)
  const [copyingIndex, setCopyingIndex] = useState<number | null>(null)
  const [skippedDays, setSkippedDays] = useState<string[]>([])

  const countForDay = (weekday: number) => shifts.filter((s) => s.weekday === weekday).length

  // A 9-5 five-day week previously meant setting five identical rows by hand — this copies one
  // shift's time+timezone onto whichever other days are picked, skipping any day already at the
  // per-day cap rather than blocking the whole action. `skippedDays` is what says so out loud —
  // dropping a day with no note reads as the copy having quietly failed on it.
  const copyShiftTo = (index: number, targetWeekdays: number[]) => {
    const source = shifts[index]
    if (!source) return
    const eligible = targetWeekdays.filter((weekday) => weekday !== source.weekday)
    const toCopy = eligible.filter((weekday) => countForDay(weekday) < MAX_SHIFTS_PER_DAY)
    setShifts((current) => [
      ...current,
      ...toCopy.map((weekday) => ({ weekday, startMinute: source.startMinute, endMinute: source.endMinute, timezone: source.timezone })),
    ])
    setCopyingIndex(null)
    setSkippedDays(
      eligible
        .filter((weekday) => !toCopy.includes(weekday))
        .map((weekday) => WEEKDAYS[weekday]),
    )
  }

  const addShift = (weekday: number) => {
    if (countForDay(weekday) >= MAX_SHIFTS_PER_DAY) return
    setShifts((current) => [
      ...current,
      { weekday, startMinute: 9 * 60, endMinute: 17 * 60, timezone: current[0]?.timezone ?? browserTimezone() },
    ])
  }

  const updateShift = (index: number, patch: Partial<ShiftSlotView>) =>
    setShifts((current) => current.map((s, i) => (i === index ? { ...s, ...patch } : s)))

  const removeShift = (index: number) => setShifts((current) => current.filter((_, i) => i !== index))

  return (
    <div className="stack gap-3" style={{ marginTop: '20px', borderTop: '1px solid var(--md-outline)', paddingTop: '16px' }}>
      <div className="stack gap-1">
        <span className="cad-label">Shift schedule</span>
        <p className="cad-muted">
          Gates round-robin assignment to whoever is on shift. Leave empty and this person is
          treated as always available, same as before shifts existed.
        </p>
      </div>

      {WEEKDAYS.map((day, weekday) => (
        <div key={day} className="stack gap-2">
          <div className="row between wrap gap-2" style={{ alignItems: 'center' }}>
            <span className="cad-label">{day}</span>
            <Button
              variant="text"
              disabled={countForDay(weekday) >= MAX_SHIFTS_PER_DAY}
              onClick={() => addShift(weekday)}
            >
              + Add shift
            </Button>
          </div>
          {shifts.map((shift, index) =>
            shift.weekday === weekday ? (
              <div key={index} className="stack gap-2">
                <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
                  <input
                    type="time"
                    value={minutesToTime(shift.startMinute)}
                    onChange={(e) => updateShift(index, { startMinute: timeToMinutes(e.target.value) })}
                  />
                  <span className="cad-muted">to</span>
                  <input
                    type="time"
                    value={minutesToTime(shift.endMinute)}
                    onChange={(e) => updateShift(index, { endMinute: timeToMinutes(e.target.value) })}
                  />
                  <input
                    style={{ width: '180px' }}
                    value={shift.timezone}
                    onChange={(e) => updateShift(index, { timezone: e.target.value })}
                    placeholder="IANA timezone, e.g. Asia/Kolkata"
                  />
                  <Button variant="text" onClick={() => setCopyingIndex(copyingIndex === index ? null : index)}>
                    Copy to…
                  </Button>
                  <Button variant="text" onClick={() => removeShift(index)}>
                    Remove
                  </Button>
                </div>
                {copyingIndex === index && (
                  <div className="row gap-2 wrap" style={{ alignItems: 'center', paddingLeft: '4px' }}>
                    <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>Copy this time to:</span>
                    <Button variant="text" onClick={() => copyShiftTo(index, [1, 2, 3, 4, 5])}>
                      Weekdays
                    </Button>
                    {WEEKDAYS.map((day, targetWeekday) =>
                      targetWeekday === weekday ? null : (
                        <button
                          key={day}
                          type="button"
                          className="cad-pill"
                          onClick={() => copyShiftTo(index, [targetWeekday])}
                        >
                          {day}
                        </button>
                      ),
                    )}
                  </div>
                )}
              </div>
            ) : null,
          )}
        </div>
      ))}

      {skippedDays.length > 0 && (
        <p className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
          Skipped {skippedDays.join(', ')} — already at {MAX_SHIFTS_PER_DAY} shifts that day.
        </p>
      )}

      <div className="row gap-2">
        <Button disabled={pending} onClick={() => onSave(shifts)}>
          {pending ? 'Saving…' : 'Save shifts'}
        </Button>
      </div>
    </div>
  )
}

/**
 * The middle tier between "everyone with label access" and one named person: a chat can be
 * assigned to a team first, then optionally to one member of it. Teams are just a name and a
 * member list — assignment itself still happens from the Inbox's own assign picker, this is
 * only where the groupings get defined.
 */
function TeamGroupsSection({
  teams,
  members,
  onChanged,
}: {
  teams: TeamGroup[]
  members: TeamMemberView[]
  onChanged: () => void
}) {
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [memberIds, setMemberIds] = useState<string[]>([])
  const [editingId, setEditingId] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: () => api.createTeamGroup({ name: name.trim(), memberIds }),
    onSuccess: () => {
      setCreating(false)
      setName('')
      setMemberIds([])
      onChanged()
    },
  })
  const update = useMutation({
    mutationFn: (input: { id: string; name: string; memberIds: string[] }) =>
      api.updateTeamGroup(input.id, { name: input.name, memberIds: input.memberIds }),
    onSuccess: () => {
      setEditingId(null)
      onChanged()
    },
  })
  const remove = useMutation({ mutationFn: api.deleteTeamGroup, onSuccess: onChanged })

  const nameOf = (userId: string) => members.find((m) => m.userId === userId)?.name ?? 'Former member'

  return (
    <Card variant="outlined">
      <div className="stack gap-4">
        <div className="row between wrap gap-3" style={{ alignItems: 'baseline' }}>
          <div className="stack gap-1">
            <h2 className="cad-section-title">Teams</h2>
            <p className="cad-muted" style={{ maxWidth: '58ch', margin: 0 }}>
              A tier above one named person — assign a chat to Sales, Marketing or Support first,
              then optionally to one member of that team from the Inbox.
            </p>
          </div>
          {!creating && (
            <Button variant="outlined" onClick={() => setCreating(true)}>
              New team
            </Button>
          )}
        </div>

        {/* `create.error` is rendered inside the create form, but renaming or deleting a team
            could fail with no feedback at all — the row simply stopped spinning. */}
        {(update.error || remove.error) && (
          <Banner tone="error">
            {(() => {
              const err = update.error ?? remove.error
              return err instanceof ApiError || err instanceof Error
                ? err.message
                : 'That change did not go through.'
            })()}
          </Banner>
        )}

        {teams.length === 0 && !creating && (
          <p className="cad-muted">
            No teams yet — a team lets you assign a chat to Sales or Support before picking a
            person, so nothing waits on one named individual.
          </p>
        )}

        {teams.map((team) =>
          editingId === team.id ? (
            <TeamGroupEditor
              key={team.id}
              team={team}
              members={members}
              pending={update.isPending}
              onCancel={() => setEditingId(null)}
              onSave={(next) => update.mutate({ id: team.id, ...next })}
            />
          ) : (
            <div key={team.id} className="row between wrap gap-3" style={{ alignItems: 'center' }}>
              <div className="stack gap-1">
                <span style={{ fontWeight: 600 }}>{team.name}</span>
                <span className="cad-muted" style={{ fontSize: 'var(--cad-text-md)' }}>
                  {team.memberIds.length === 0
                    ? 'No members yet'
                    : team.memberIds.map(nameOf).join(', ')}
                </span>
              </div>
              <div className="row gap-2">
                <Button variant="text" onClick={() => setEditingId(team.id)}>
                  Edit
                </Button>
                <Button
                  variant="text"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (confirm(`Delete the "${team.name}" team? Chats already assigned to it keep that assignment.`)) {
                      remove.mutate(team.id)
                    }
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>
          ),
        )}

        {creating && (
          <form
            className="stack gap-3"
            onSubmit={(event: FormEvent) => {
              event.preventDefault()
              if (name.trim()) create.mutate()
            }}
          >
            <div className="cad-field">
              <label htmlFor="new-team-name">Team name</label>
              <input
                id="new-team-name"
                className="cad-input"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Sales, Marketing, Support…"
              />
            </div>
            <div className="stack gap-2">
              <span className="cad-label">Members</span>
              <div className="row gap-2 wrap">
                {members.map((member) => (
                  <label key={member.userId} className="row gap-1" style={{ alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={memberIds.includes(member.userId)}
                      onChange={(event) =>
                        setMemberIds((prev) =>
                          event.target.checked ? [...prev, member.userId] : prev.filter((id) => id !== member.userId),
                        )
                      }
                    />
                    {member.name}
                  </label>
                ))}
              </div>
            </div>
            {create.error && (
              <Banner tone="error">
                {create.error instanceof ApiError ? create.error.message : 'Could not create the team.'}
              </Banner>
            )}
            <div className="row gap-2">
              <Button type="submit" disabled={!name.trim() || create.isPending}>
                {create.isPending ? 'Creating…' : 'Create team'}
              </Button>
              <Button
                variant="text"
                type="button"
                onClick={() => {
                  setCreating(false)
                  setName('')
                  setMemberIds([])
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}
      </div>
    </Card>
  )
}

function TeamGroupEditor({
  team,
  members,
  pending,
  onSave,
  onCancel,
}: {
  team: TeamGroup
  members: TeamMemberView[]
  pending: boolean
  onSave: (next: { name: string; memberIds: string[] }) => void
  onCancel: () => void
}) {
  const [name, setName] = useState(team.name)
  const [memberIds, setMemberIds] = useState<string[]>(team.memberIds)

  return (
    <div className="stack gap-3" style={{ padding: '12px', background: 'var(--cad-surface-sunken)', borderRadius: 'var(--cad-radius)' }}>
      <div className="cad-field">
        <label htmlFor={`team-name-${team.id}`}>Team name</label>
        <input
          id={`team-name-${team.id}`}
          className="cad-input"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </div>
      <div className="row gap-2 wrap">
        {members.map((member) => (
          <label key={member.userId} className="row gap-1" style={{ alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={memberIds.includes(member.userId)}
              onChange={(event) =>
                setMemberIds((prev) =>
                  event.target.checked ? [...prev, member.userId] : prev.filter((id) => id !== member.userId),
                )
              }
            />
            {member.name}
          </label>
        ))}
      </div>
      <div className="row gap-2">
        <Button disabled={!name.trim() || pending} onClick={() => onSave({ name: name.trim(), memberIds })}>
          {pending ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="text" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  )
}
